import { Runtype } from "runtypes";

import type {
  OutputMP,
  ResolvedOutputContentMP,
} from "../redux/slices/appSessionMPSlice";

import { logErrorMsg } from "./logging";

type ParseSuccess<T> = {
  success: true;
  value: T;
};

type ParseFailure = {
  success: false;
  error?: unknown;
  errorMessage: string;
  errorDetails?: Record<string, unknown>;
};

export type ParseResult<T> = ParseSuccess<T> | ParseFailure;

function tryParseOutput<T>(
  runtype: Runtype<T>,
  contents: string | undefined,
): ParseResult<T> {
  if (!contents) {
    return {
      success: false,
      errorMessage: "empty message contents",
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(contents);
  } catch (error) {
    return {
      success: false,
      error,
      errorMessage: "invalid JSON",
    };
  }

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      return {
        success: false,
        error,
        errorMessage: "invalid nested JSON",
      };
    }
  }

  const validationResult = runtype.validate(parsed);

  if (validationResult.success) {
    return { success: true, value: validationResult.value };
  } else {
    return {
      success: false,
      error: new Error(validationResult.message),
      errorMessage: "runtype validation failed",
      errorDetails: {
        validationDetails: validationResult.message,
      },
    };
  }
}

type ParseOutputResult<T> = { success: true; value: T } | { success: false };

export function parseOutputOrLog<T>(
  runtype: Runtype<T>,
  content: Pick<ResolvedOutputContentMP, "id" | "mimeType" | "contents">,
  output: Pick<OutputMP, "id" | "appSessionCellId">,
): ParseOutputResult<T> {
  const result = tryParseOutput(runtype, content.contents);
  if (result.success) {
    return { success: true, value: result.value };
  }

  const errorToLog = result.error ?? new Error(result.errorMessage);

  logErrorMsg(errorToLog, `Output parsing failed: ${result.errorMessage}`, {
    safe: {
      expectedType: runtype.toString(),
      outputContentId: content.id,
      outputId: output.id,
      appSessionCellId: output.appSessionCellId,
      mimeType: content.mimeType,
      ...(result.errorDetails ?? {}),
    },
  });

  return { success: false };
}

export function assertOutputOrLog<T>(
  runtype: Runtype<T>,
  content: Pick<ResolvedOutputContentMP, "id" | "mimeType" | "contents">,
  output: Pick<OutputMP, "id" | "appSessionCellId">,
): T {
  const result = tryParseOutput(runtype, content.contents);
  if (result.success) {
    return result.value;
  }

  const errorToLog = result.error ?? new Error(result.errorMessage);

  logErrorMsg(errorToLog, `Output parsing failed: ${result.errorMessage}`, {
    safe: {
      expectedType: runtype.toString(),
      outputContentId: content.id,
      outputId: output.id,
      appSessionCellId: output.appSessionCellId,
      mimeType: content.mimeType,
      ...(result.errorDetails ?? {}),
    },
  });

  throw errorToLog;
}
